/**
 * SCORM Launch Coordinator
 * Handles the complete SCORM launch flow
 * Backend now manages the entire flow via redirects
 */

import { ScormError } from "./errors";
import type { ScormContent } from "./types";
import { ScormErrorCodes } from "./types";

// Track launch windows for cleanup
const launchWindows = new Map<string, Window>();

export interface LaunchOptions {
  /** Whether to open in new window/tab (default: true) */
  openInNewWindow?: boolean;
  /** Window features for new window (default: basic popup features) */
  windowFeatures?: string;
  /** Target window name (default: auto-generated) */
  windowTarget?: string;
  /** Callback when launch succeeds */
  onLaunchSuccess?: (attemptId: string, launchUrl: string) => void;
  /** Callback when launch fails */
  onLaunchError?: (error: Error) => void;
  /** Callback when SCORM session ends */
  onSessionEnd?: (attemptId: string) => void;
}

export interface LaunchResult {
  success: boolean;
  attemptId?: string;
  launchUrl?: string;
  error?: Error;
  window?: Window;
}

/**
 * Launch a SCORM package with full session management
 */
export async function launchScormContent(
  content: ScormContent,
  options: LaunchOptions = {}
): Promise<LaunchResult> {
  const {
    openInNewWindow = true,
    windowFeatures = "",
    windowTarget = "_blank",
    onLaunchSuccess,
    onLaunchError,
    onSessionEnd,
  } = options;

  try {
    console.log("Starting SCORM launch for:", {
      packageId: content.package_id,
      contentGuid: content.guid,
      title: content.title,
    });

    // Backend now handles everything via redirect - just navigate to the launch endpoint
    // Backend will:
    // 1. Create/resume attempt
    // 2. Redirect to wrapper with content_guid parameter
    // 3. Wrapper will fetch launch data from backend
    const launchUrl = `${window.env.CISCO_URL}/scorm/launch/${content.guid}`;

    console.log("🚀 Launching SCORM via backend redirect:", launchUrl);

    // Set up window management
    let launchWindow: Window | null = null;

    if (openInNewWindow) {
      // Open backend launch URL in new window/tab
      launchWindow = window.open(launchUrl, windowTarget, windowFeatures);

      if (!launchWindow) {
        throw new ScormError(
          ScormErrorCodes.GENERAL_EXCEPTION,
          "Failed to open SCORM content window. Popup may have been blocked."
        );
      }

      // Store window reference using content GUID as key (we don't have attemptId yet)
      launchWindows.set(content.guid, launchWindow);

      // Set up basic window monitoring
      setupWindowMonitoring(content.guid, launchWindow, onSessionEnd);
    } else {
      // Navigate current window to backend launch URL
      window.location.href = launchUrl;
    }

    console.log("SCORM launch initiated successfully:", {
      contentGuid: content.guid,
      launchUrl: launchUrl,
    });

    // Trigger success callback (we don't have attemptId until backend creates it)
    if (onLaunchSuccess) {
      onLaunchSuccess(content.guid, launchUrl);
    }

    return {
      success: true,
      attemptId: content.guid, // Use GUID as identifier until backend assigns attemptId
      launchUrl: launchUrl,
      window: launchWindow || undefined,
    };
  } catch (error) {
    console.error("SCORM launch failed:", error);

    const scormError =
      error instanceof ScormError
        ? error
        : new ScormError(
            ScormErrorCodes.GENERAL_EXCEPTION,
            `Launch failed: ${error}`
          );

    // Trigger error callback
    if (onLaunchError) {
      onLaunchError(scormError);
    }

    return {
      success: false,
      error: scormError,
    };
  }
}

/**
 * Monitor SCORM window and handle cleanup when closed
 */
function setupWindowMonitoring(
  sessionId: string,
  launchWindow: Window,
  onSessionEnd?: (sessionId: string) => void
): void {
  const checkWindowClosed = async () => {
    if (launchWindow.closed) {
      console.log("SCORM window closed, cleaning up session:", sessionId);

      // Clean up session
      cleanupSession(sessionId);

      // Trigger session end callback
      if (onSessionEnd) {
        onSessionEnd(sessionId);
      }

      return;
    }

    // Check again in 1 second
    setTimeout(checkWindowClosed, 1000);
  };

  // Start monitoring
  setTimeout(checkWindowClosed, 1000);
}

/**
 * Clean up a SCORM session
 */
export function cleanupSession(sessionId: string): void {
  // Close and remove window reference
  const window = launchWindows.get(sessionId);
  if (window && !window.closed) {
    window.close();
  }
  launchWindows.delete(sessionId);
}

/**
 * Get all active SCORM sessions
 */
export function getActiveSessions(): string[] {
  return Array.from(launchWindows.keys());
}

/**
 * Clean up all SCORM sessions (useful for app cleanup)
 */
export function cleanupAllSessions(): void {
  for (const attemptId of launchWindows.keys()) {
    cleanupSession(attemptId);
  }
}
